import { LibraTicket } from './libraTicketsApi'

function getAssessmentKey(ticket: LibraTicket): string {
  return ticket.assessmentId || ticket.id
}

export function groupTicketsByAssessment(
  tickets: ReadonlyArray<LibraTicket>
): ReadonlyMap<string, ReadonlyArray<LibraTicket>> {
  const grouped = new Map<string, LibraTicket[]>()

  tickets.forEach((ticket) => {
    const key = getAssessmentKey(ticket)
    const current = grouped.get(key) ?? []
    current.push(ticket)
    grouped.set(key, current)
  })

  return grouped
}

function compareTicketRecency(a: LibraTicket, b: LibraTicket): number {
  const updatedCompare = (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
  if (updatedCompare !== 0) {
    return updatedCompare
  }
  return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
}

function getTicketVersionValue(ticket: LibraTicket): number {
  const rawVersion = ticket.version
  return typeof rawVersion === 'number' && Number.isFinite(rawVersion)
    ? rawVersion
    : 1
}

function compareTicketVersionThenRecency(
  a: LibraTicket,
  b: LibraTicket
): number {
  const aVersion = getTicketVersionValue(a)
  const bVersion = getTicketVersionValue(b)
  if (aVersion !== bVersion) {
    return bVersion - aVersion
  }
  return compareTicketRecency(a, b)
}

export function getLatestVersions(
  tickets: ReadonlyArray<LibraTicket>
): ReadonlyArray<LibraTicket> {
  const grouped = groupTicketsByAssessment(tickets)
  const latest: LibraTicket[] = []

  grouped.forEach((versions) => {
    const newest = [...versions].sort(compareTicketVersionThenRecency)[0]

    if (newest) {
      latest.push(newest)
    }
  })

  return latest.sort(compareTicketRecency)
}

export function getVersionHistory(
  allTickets: ReadonlyArray<LibraTicket>,
  assessmentId: string
): ReadonlyArray<LibraTicket> {
  return allTickets
    .filter((ticket) => getAssessmentKey(ticket) === assessmentId)
    .sort(compareTicketVersionThenRecency)
}

/**
 * Returns one ticket per asset for dashboard stats:
 * - Prefer latest completed ticket for the asset
 * - Fallback to latest ticket if no completed ticket exists
 */
export function getLatestTicketPerAssetForStats(
  tickets: ReadonlyArray<LibraTicket>
): ReadonlyArray<LibraTicket> {
  const ticketsByUnit = new Map<string, LibraTicket[]>()

  tickets.forEach((ticket) => {
    const unitId = ticket.tags.unit
    if (!unitId) {
      return
    }
    const current = ticketsByUnit.get(unitId) ?? []
    current.push(ticket)
    ticketsByUnit.set(unitId, current)
  })

  const selected: LibraTicket[] = []
  ticketsByUnit.forEach((unitTickets) => {
    const completed = unitTickets.filter((ticket) => ticket.workflowComplete)
    const source = completed.length > 0 ? completed : unitTickets
    const latest = [...source].sort(compareTicketVersionThenRecency)[0]
    if (latest) {
      selected.push(latest)
    }
  })

  return selected.sort(compareTicketRecency)
}
